import React from 'react';
import { useTranslation } from 'react-i18next';
import * as Sentry from '@sentry/react';
import { useNavigate } from 'react-router-dom';
import { isReauthRequired } from '@/lib/reauth';
import { extractReauthError } from '@/lib/reauth';
import { useReauth } from '@/contexts/ReauthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Store, ExternalLink, Plus, ChevronDown, ChevronRight } from 'lucide-react';
import { useSelection } from '@/contexts/SelectionContext';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { MerchantCardSkeleton } from '@/components/skeletons/MerchantCardSkeleton';
import { toast } from 'sonner';
import { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import SyncProgressDialog from '@/components/SyncProgressDialog';
import { PageHeader } from '@/components/PageHeader';
import { collectEmbedded, asMemberRole, type McMemberRow } from '@/lib/mcMembership';
import { McMembersCard } from '@/components/settings/McMembersCard';
import { ErrorState } from '@/components/ErrorState';
interface MerchantCenter {
  id: string;
  merchant_center_id: string;
  name: string;
  website_url: string;
  shop_description?: string | null;
  shop_description_generated_at?: string | null;
  parent_merchant_center_id?: string | null;
  product_count?: number;
  feed_count?: number;
  /**
   * Mitgliedschafts-Umstellung (Task 10, fuer Task 11): die Rolle DIESES
   * Nutzers an der MC, aus dem Grant unter `selectedGoogleAccountId`. `null`
   * ist der Fehlerfall (Zeile ohne aufgeloeste Rolle) und wird nie erwartet,
   * weil der Query-Filter `.eq('google_account_id', ...)` genau EINE
   * Mitgliedschaftszeile je MC liefert. Typ kommt aus `McMemberRow['role']`
   * (Fix-Runde 1, Minor 1) statt einer eigenen literalen Union, damit beide
   * Stellen nie auseinanderlaufen.
   */
  role?: McMemberRow['role'] | null;
}

const MerchantCenters: React.FC = () => {
  const { t } = useTranslation('settings');
  const {
    user
  } = useAuth();
  const navigate = useNavigate();
  const { triggerReauth } = useReauth();
  const {
    selectedGoogleAccountId,
    setSelectedMerchantCenter,
    selectedMerchantCenterId
  } = useSelection();
  const [merchantId, setMerchantId] = useState('');
  const [registering, setRegistering] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [expandedParents, setExpandedParents] = useState<Set<string>>(new Set());
  const [expandedDescriptions, setExpandedDescriptions] = useState<Set<string>>(new Set());
  const [showSyncDialog, setShowSyncDialog] = useState(false);

  const toggleDescriptionExpansion = (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setExpandedDescriptions(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const {
    data: merchantCenters,
    isLoading,
    isError,
    refetch
  } = useQuery({
    queryKey: ['merchant-centers', selectedGoogleAccountId],
    queryFn: async () => {
      if (!selectedGoogleAccountId) return [];
      // Mitgliedschafts-Umstellung (Task 10): "MCs dieses Google-Kontos" heisst
      // seit der Spec "MCs, fuer die dieses Konto der Grant einer Mitgliedschaft
      // ist" -- die Basistabelle ist deshalb merchant_center_members statt
      // merchant_centers direkt. `!inner` auf merchant_centers filtert
      // Mitgliedschaftszeilen ohne sichtbare MC weg; die Einbettung bleibt
      // defensiv per firstEmbedded entpackt, fuer den Fall dass sie statt eines
      // Objekts ein Array liefert.
      //
      // K05: kein products(count) im Embed mehr. Der Zaehler lief je Feed unter
      // zeilenweiser RLS und riss fuer Agenturkonten das 8-s-statement_timeout
      // (81 MCs, 174 Feeds); die Seite zeigte dann den Leerzustand. Die Zahlen
      // kommen aus feed_product_counts() (109 ms warm fuer dasselbe Konto).
      const [members, counts] = await Promise.all([
        supabase
          .from('merchant_center_members')
          .select('role, merchant_centers!inner(*, feeds(id))')
          .eq('google_account_id', selectedGoogleAccountId),
        supabase.rpc('feed_product_counts'),
      ]);
      if (members.error) throw members.error;
      // Nie eine halbe Antwort als "0 Produkte" rendern.
      if (counts.error) throw counts.error;

      const countByFeed = new Map<string, number>(
        (counts.data ?? []).map((r) => [r.feed_id, Number(r.product_count)] as const),
      );

      const rows = members.data ?? [];

      // Flatten + dedupe nach MC-ID (derselbe Nutzer sollte je MC hoechstens
      // einen Grant unter diesem Google-Konto haben, siehe UNIQUE(merchant_center_id,
      // user_id) der Migration -- die Dedupe ist trotzdem defensiv), dann Rolle
      // anhaengen und Zaehler berechnen wie zuvor. Flatten/Dedupe-Kern liegt
      // seit Fix-Runde 1 (Important 1) in `collectEmbedded`; die Rollen-Ableitung
      // laeuft ueber `asMemberRole` (Minor 5) statt eines unkontrollierten
      // `as`-Casts.
      const merchantsWithCounts: MerchantCenter[] = collectEmbedded(
        rows,
        (row) => row.merchant_centers,
        (_row, mc) => mc.id,
      ).map(({ row, embed }) => {
        const { feeds, ...rest } = embed;
        return {
          ...rest,
          role: asMemberRole(row.role),
          feed_count: feeds?.length || 0,
          product_count: (feeds ?? []).reduce((total: number, feed) => total + (countByFeed.get(feed.id) ?? 0), 0),
        };
      });
      merchantsWithCounts.sort((a, b) => a.name.localeCompare(b.name));
      return merchantsWithCounts;
    },
    enabled: !!selectedGoogleAccountId
  });

  // hasFeed derived from the feed_count already loaded by the merchantCenters
  // query above — avoids a second round-trip that was adding ~1s of waterfall
  // latency on accounts with many merchant centers.
  const merchantCenterIdsWithFeeds = new Set(
    (merchantCenters || []).filter(mc => (mc.feed_count ?? 0) > 0).map(mc => mc.id)
  );
  const hasFeed = (merchantId: string) => merchantCenterIdsWithFeeds.has(merchantId);
  const handleSelectMerchant = async (merchantId: string) => {
    if (!hasFeed(merchantId)) {
      toast.error(t('merchantCenters.noFeedsAvailable'), {
        description: t('merchantCenters.noFeedsAvailableDesc'),
      });
      return;
    }
    await setSelectedMerchantCenter(merchantId);
    toast.success(t('merchantCenters.selected'), {
      description: t('merchantCenters.selectedDesc'),
    });
  };
  const registerMerchantCenter = async () => {
    if (!merchantId.trim() || !selectedGoogleAccountId) {
      toast.error(t('merchantCenters.missingInfo'), {
        description: t('merchantCenters.missingInfoDesc'),
      });
      return;
    }
    setRegistering(true);
    try {
      const res = await supabase.functions.invoke('register-merchant-developer', {
        body: {
          merchantCenterId: merchantId.trim(),
          googleAccountId: selectedGoogleAccountId
        }
      });

      if (isReauthRequired(res)) {
        const reauthError = await extractReauthError(res);
        triggerReauth(reauthError || { googleAccountId: selectedGoogleAccountId || '' });
        return;
      }

      if (res.error) throw res.error;
      toast.success(t('merchantCenters.registrationSuccess'), {
        description: t('merchantCenters.registrationSuccessDesc'),
      });
      setMerchantId('');
      await refreshData();
    } catch (error) {
      console.error('Error registering merchant center:', error);
      Sentry.captureException(error, { tags: { feature: 'merchant-centers', action: 'register' } });
      toast.error(t('merchantCenters.registrationFailed'), {
        description: error instanceof Error ? error.message : t('merchantCenters.registrationFailedDesc'),
      });
    } finally {
      setRegistering(false);
    }
  };
  const refreshData = async () => {
    if (!selectedGoogleAccountId) {
      toast.error(t('merchantCenters.noGoogleAccount'), {
        description: t('merchantCenters.noGoogleAccountDesc'),
      });
      return;
    }
    setShowSyncDialog(true);
    setRefreshing(true);
    try {
      const res = await supabase.functions.invoke('fetch-account-data', {
        body: {
          googleAccountId: selectedGoogleAccountId,
          userId: user?.id
        }
      });

      // Check for token expiration in response data first (even with error status)
      if (res.data?.code === 'GOOGLE_REAUTH_REQUIRED') {
        setShowSyncDialog(false);
        sessionStorage.setItem('redirectAfterReauth', window.location.pathname);
        toast.error(t('merchantCenters.reconnectionRequired'), {
          description: <span>
              {t('merchantCenters.reconnectionRequiredDesc')}{' '}
              <a href="/settings?tab=accounts" className="text-primary underline">{t('googleAccounts.title')}</a>.
            </span>,
        });
        setTimeout(() => navigate('/settings?tab=accounts'), 1500);
        return;
      }
      if (res.error) {
        setShowSyncDialog(false);
        throw res.error;
      }
      if (res.data?.success === false) {
        setShowSyncDialog(false);
        toast.error(t('merchantCenters.refreshFailed'), {
          description: res.data.message || t('merchantCenters.refreshFailedDesc'),
        });
        return;
      }

      // Let the dialog handle completion via polling and onComplete callback
      // No need to manually close or show toast here
    } catch (error: any) {
      setShowSyncDialog(false);
      if (error?.code === 'GOOGLE_REAUTH_REQUIRED') {
        sessionStorage.setItem('redirectAfterReauth', window.location.pathname);
        toast.error(t('merchantCenters.reconnectionRequired'), {
          description: <span>
              {t('merchantCenters.reconnectionRequiredDesc')}{' '}
              <a href="/settings?tab=accounts" className="text-primary underline">{t('googleAccounts.title')}</a>.
            </span>,
        });
        setTimeout(() => navigate('/settings?tab=accounts'), 1500);
        return;
      }
      console.error('Error refreshing data:', error);
      Sentry.captureException(error, { tags: { feature: 'merchant-centers', action: 'refresh' } });
      toast.error(t('merchantCenters.refreshFailed'), {
        description: error instanceof Error ? error.message : t('merchantCenters.refreshFailedDesc'),
      });
    } finally {
      setRefreshing(false);
    }
  };
  const hasMerchants = merchantCenters && merchantCenters.length > 0;

  // Group parent and sub-accounts
  const parentAccounts = merchantCenters?.filter(m => !m.parent_merchant_center_id) || [];
  const getSubAccounts = (parentId: string) => merchantCenters?.filter(m => m.parent_merchant_center_id === parentId) || [];
  const toggleParentExpansion = (parentId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setExpandedParents(prev => {
      const newSet = new Set(prev);
      if (newSet.has(parentId)) {
        newSet.delete(parentId);
      } else {
        newSet.add(parentId);
      }
      return newSet;
    });
  };
  const renderMerchantCard = (merchant: MerchantCenter, isSubAccount = false, hasSubAccounts = false) => {
    const isExpanded = expandedParents.has(merchant.id);
    const hasFeeds = hasFeed(merchant.id);
    const subAccounts = getSubAccounts(merchant.id);
    const hasSubAccountsWithFeeds = subAccounts.some(sub => hasFeed(sub.id));
    const isSelectable = hasFeeds;
    return <Card key={merchant.id} className={`transition-all ${isSelectable ? 'hover:shadow-md cursor-pointer' : 'opacity-60 cursor-not-allowed'} ${selectedMerchantCenterId === merchant.id ? 'ring-2 ring-primary' : ''} ${isSubAccount ? 'ml-8 border-l-4 border-l-primary/30' : ''} ${isExpanded && hasSubAccounts ? 'ring-2 ring-primary/30' : ''}`} onClick={() => isSelectable && handleSelectMerchant(merchant.id)}>
        <CardHeader>
          <div className="flex items-start justify-between">
            <div className="flex items-center space-x-3 flex-1">
              {hasSubAccounts && <button
                  onClick={e => toggleParentExpansion(merchant.id, e)}
                  className="p-1 hover:bg-primary/10 rounded transition-colors"
                  aria-label={isExpanded ? t('merchantCenters.collapseSubAccounts') : t('merchantCenters.expandSubAccounts')}
                >
                  {isExpanded ? <ChevronDown className="h-4 w-4 text-primary" /> : <ChevronRight className="h-4 w-4 text-muted-foreground" />}
                </button>}
              <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                <Store className="h-5 w-5 text-primary" />
              </div>
              <div className="flex-1 min-w-0">
                <CardTitle className="text-base">
                  <span className="block truncate">{merchant.name}</span>
                </CardTitle>
                <CardDescription className="text-xs flex items-center gap-2 flex-wrap mt-1">
                  <span>{t('merchantCenters.idLabel', { id: merchant.merchant_center_id })}</span>
                  {merchant.product_count !== undefined && merchant.product_count > 0 && <Badge variant="secondary" className="shrink-0">
                      {t('merchantCenters.productCount', { count: merchant.product_count })}
                    </Badge>}
                  {merchant.feed_count !== undefined && merchant.feed_count > 0 && <span className="text-muted-foreground">
                      {t('merchantCenters.feedCount', { count: merchant.feed_count })}
                    </span>}
                </CardDescription>
              </div>
            </div>
            {selectedMerchantCenterId === merchant.id && <Badge variant="default">{t('merchantCenters.selectedBadge')}</Badge>}
          </div>
        </CardHeader>
        <CardContent className="space-y-2">
          {merchant.shop_description && (() => {
            const isLong = merchant.shop_description.length > 120;
            const isExpanded = expandedDescriptions.has(merchant.id);
            return <div className="space-y-1">
              <p className={`text-sm text-muted-foreground ${isLong && !isExpanded ? 'line-clamp-2' : ''}`}>
                {merchant.shop_description}
              </p>
              {isLong && <button
                onClick={(e) => toggleDescriptionExpansion(merchant.id, e)}
                className="text-xs text-primary hover:underline"
              >
                {isExpanded ? t('merchantCenters.showLess') : t('merchantCenters.showMore')}
              </button>}
            </div>;
          })()}
          {!merchant.shop_description && merchant.website_url && <p className="text-xs text-muted-foreground italic">
              {t('merchantCenters.shopDescriptionPending')}
            </p>}
          {merchant.website_url && <div className="flex items-center gap-2 text-xs">
              <ExternalLink className="h-3 w-3 text-muted-foreground" />
              <a href={merchant.website_url} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline truncate" onClick={e => e.stopPropagation()}>
                {merchant.website_url}
              </a>
            </div>}
          {isSubAccount && <Badge variant="outline" className="text-xs">
              {t('merchantCenters.subAccount')}
            </Badge>}
          {!hasFeeds && !isSubAccount && hasSubAccountsWithFeeds && <Badge variant="secondary" className="text-xs">
              {t('merchantCenters.managingAccount')}
            </Badge>}
          {!hasFeeds && !hasSubAccountsWithFeeds && <Badge variant="destructive" className="text-xs">
              {t('merchantCenters.noFeedsAvailableBadge')}
            </Badge>}
          {/* Task 11: Mitglieder-Karte, sichtbar fuer Eigentuemer UND Mitglieder
              (McMembersCard rendert selbst nichts, solange sie keine Mitglieder
              geladen hat). currentUserId aus useAuth() -- nur die eigene Rolle
              entscheidet, ob der Uebertragen-Knopf erscheint. */}
          <McMembersCard mcId={merchant.id} currentUserId={user?.id} />
        </CardContent>
      </Card>;
  };
  // K05, Nachbesserung N2: in TanStack Query v5 wird isError auch bei einem
  // gescheiterten Refetch wahr, data behaelt dabei die alte Liste. Der
  // Fehlerzustand unten ersetzt deshalb nur eine Liste, die nie geladen wurde
  // (`isError && !merchantCenters`); ein gescheiterter Refetch laesst eine
  // gueltige Liste stehen. Ausloeser eines Refetch sind hier Invalidierungen
  // (McMembersCard.tsx, GoogleAccounts.tsx), ein erneutes Oeffnen nach Ablauf
  // der staleTime, ein Reconnect und der ausdrueckliche refetch() (Erneut
  // versuchen, Abschluss des Syncs). Der Fensterfokus gehoert nicht dazu:
  // lib/queryClient.ts schaltet refetchOnWindowFocus global ab.
  return <div className="space-y-6">
      <PageHeader
        title={t('merchantCenters.title')}
        subtitle={t('merchantCenters.subtitle')}
        actions={!isLoading && !!selectedGoogleAccountId && hasMerchants ? <Button onClick={refreshData} disabled={refreshing} variant="outline" className="w-full sm:w-auto">
            {refreshing ? t('actions.refreshing', { ns: 'common' }) : t('actions.refreshData', { ns: 'common' })}
          </Button> : undefined}
        as="h2"
      />

      {/* Register Merchant Center - placeholder removed, use wizard or refresh instead */}

      {!selectedGoogleAccountId ? <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Store className="h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">{t('merchantCenters.noGoogleAccount')}</h3>
            <p className="text-sm text-muted-foreground text-center">
              {t('merchantCenters.noGoogleAccountPageDesc')}
            </p>
          </CardContent>
        </Card> : isLoading ? <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3].map(i => <MerchantCardSkeleton key={i} />)}
        </div> : isError && !merchantCenters ? <ErrorState
          title={t('merchantCenters.loadErrorTitle')}
          description={t('merchantCenters.loadErrorDesc')}
          onRetry={() => { void refetch(); }}
        /> : !hasMerchants ? <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Store className="h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">{t('merchantCenters.noMerchants')}</h3>
            <p className="text-sm text-muted-foreground text-center mb-4">
              {t('merchantCenters.noMerchantsDesc')}
            </p>
            <Button onClick={refreshData} variant="outline">
              {t('actions.refreshData', { ns: 'common' })}
            </Button>
          </CardContent>
        </Card> : <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {parentAccounts.map(merchant => {
        const subAccounts = getSubAccounts(merchant.id);
        const isExpanded = expandedParents.has(merchant.id);
        const hasSubAccounts = subAccounts.length > 0;
        return <React.Fragment key={merchant.id}>
                {renderMerchantCard(merchant, false, hasSubAccounts)}
                {isExpanded && subAccounts.map(subAccount => renderMerchantCard(subAccount, true, false))}
              </React.Fragment>;
      })}
        </div>}

      <SyncProgressDialog open={showSyncDialog} googleAccountId={selectedGoogleAccountId} onComplete={async () => {
      // Folge von N2: bei einem gescheiterten Refetch bleibt die alte Liste
      // stehen. Ohne Blick auf das Ergebnis meldete der Abschluss dann "Daten
      // aktualisiert" ueber einer Liste, die gar nicht neu geladen wurde.
      const result = await refetch();
      setShowSyncDialog(false);
      if (result.isError) {
        toast.error(t('merchantCenters.loadErrorTitle'), {
          description: t('merchantCenters.loadErrorDesc'),
        });
        return;
      }
      toast.success(t('merchantCenters.dataRefreshed'), {
        description: t('merchantCenters.dataRefreshedDesc'),
      });
    }} merchantCenterCount={0} feedCount={0} />
    </div>;
};
export default MerchantCenters;